import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebcontentsPresignedStorageResource } from '@libs/generated-types/webcontents';
import { omit } from 'lodash';
import { map, Observable, switchMap } from 'rxjs';
import { LanguageCode } from '../@core/data/contracts';
import { WCCreateBody, WCResponse } from '../@core/data/webcontents';
import { EnvironmentService } from '../@core/services/environment.service';
import { S3Service } from './s3.service';

@Injectable({
  providedIn: 'root',
})
export class WebcontentService<T extends WCResponse = WCResponse> {
  private readonly apiUrl: string = '';

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
    private readonly s3Service: S3Service,
  ) {
    // Use environment service to get the API URL
    this.apiUrl = this.environmentService.webcontentsServiceUrl;
  }

  getPages(parentId: string = ''): Observable<WCResponse[]> {
    const params = parentId ? { parent: parentId } : undefined;
    return this.http.get<WCResponse[]>(`${this.apiUrl}/app/webcontents`, {
      params,
    });
  }

  getById(id: string, locale: LanguageCode = LanguageCode.IT): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${id}`, { params: { locale } });
  }

  getBySlug(slug: string): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/slug/${slug}`);
  }

  save(data: WCCreateBody, id?: string): Observable<T> {
    return !!id && id !== 'new' ? this.update(id, data) : this.create(data);
  }

  create(data: WCCreateBody): Observable<T> {
    const d = omit(data, 'prev');
    return this.http.post<T>(this.apiUrl, d);
  }

  update(id: string, data: Partial<WCCreateBody>): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${id}`, data);
  }

  sort(id: string, parent: string, prev?: string | null): Observable<WCResponse> {
    return this.http.patch<WCResponse>(`${this.apiUrl}/${id}/sort`, { parent, prev });
  }

  uploadAttachments(file: File): Observable<{ success: boolean; fileKey: string; status: number }> {
    // Use the S3 upload endpoint from environment service
    const s3UploadUrl = this.apiUrl + '/presigned-url';

    // Create presigned URL first, then upload
    const body = {
      prefix: 'wecontents/attachments/',
    };

    return this.http.put<{ key: string; presignedUrl: string }>(s3UploadUrl, body).pipe(
      switchMap((response) => {
        // Now upload the file to the presigned URL
        return this.s3Service.uploadFile(response.presignedUrl, file, response.key).pipe(
          map((uploadResult) => ({
            success: uploadResult.success,
            fileKey: response.key,
            status: uploadResult.status,
          })),
        );
      }),
    );
  }

  downloadAttachment(fileKey: string): Observable<WebcontentsPresignedStorageResource> {
    const params = { fileKey };
    return this.http.get<WebcontentsPresignedStorageResource>(`${this.apiUrl}/attachment`, { params });
  }

  validateTokenAndGetMediaById(fileKey: string, token: string | undefined): Promise<{ media: WebcontentsPresignedStorageResource | null, valid: boolean }> {
    return new Promise((resolve) => {
      fetch(`${this.apiUrl}/attachment?fileKey=${fileKey}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': 'it',
          'Authorization': `Bearer ${token}`,
        },

      })
        .then(response => {
          if (response.ok) {
            resolve({ media: response.json() as unknown as WebcontentsPresignedStorageResource, valid: true });
          } else {
            resolve({ media: null, valid: false });
          }
        })
        .catch(() => {
          resolve({ media: null, valid: false });
        });
      // this.http.get<boolean>(this.apiAppUrl + '/validate-token', { params: { token } }).subscribe({
      //   next: (res) => {
      //     resolve(res);
      //   },
      //   error: (err) => {
      //     resolve(false);
      //   },
      // });

    });
  }
}
